import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { CloudinaryUploadResponse } from '@/services/cloudinary';
import { extractArticleFromUrl } from '@/services/articleExtractor';
import { useAuth } from '@/hooks/useClerkAuth';
import ImageUpload from '@/components/ImageUpload';
import { Plus, Languages, Loader2, Link as LinkIcon } from 'lucide-react';

interface ArticleCreationProps {
  onPrepareSentences: (
    title: string,
    description: string,
    germanText: string,
    imageData: CloudinaryUploadResponse | null,
    originalLink?: string
  ) => void;
  isProcessing: boolean;
}

const ArticleCreation = ({
  onPrepareSentences,
  isProcessing,
}: ArticleCreationProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [originalLink, setOriginalLink] = useState('');
  const [germanText, setGermanText] = useState('');
  const [imageData, setImageData] = useState<CloudinaryUploadResponse | null>(
    null
  );
  const [importUrl, setImportUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const { toast } = useToast();

  const handleImageUpload = (uploadedImageData: CloudinaryUploadResponse) => {
    setImageData(uploadedImageData);
  };

  const handleImageRemove = () => {
    setImageData(null);
  };

  const handleImportFromUrl = async () => {
    const url = importUrl.trim();
    if (!url) {
      toast({
        title: t('admin.import.missingUrl'),
        description: t('admin.import.missingUrlDescription'),
        variant: 'destructive',
      });
      return;
    }

    setIsImporting(true);
    try {
      const article = await extractArticleFromUrl(url, user?.id);

      setTitle(article.title);
      setDescription(article.description);
      setGermanText(article.textContent);
      setOriginalLink(article.sourceUrl);

      // If we don't already have an uploaded cover image, use the page's
      // lead image URL as a remote-hosted cover. The user can still replace
      // it via the existing image uploader.
      if (article.leadImageUrl && !imageData) {
        setImageData({
          url: article.leadImageUrl,
          secure_url: article.leadImageUrl,
          public_id: '',
        });
      }

      toast({
        title: t('admin.import.success'),
        description: article.siteName
          ? t('admin.import.successDescriptionWithSite', {
              site: article.siteName,
            })
          : t('admin.import.successDescription'),
      });
    } catch (error) {
      console.error('Article import error:', error);
      const message =
        error instanceof Error
          ? error.message
          : t('admin.import.failedDescription');
      toast({
        title: t('admin.import.failed'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handlePrepareSentences = () => {
    if (!title.trim() || !germanText.trim()) {
      toast({
        title: t('admin.missingInfo'),
        description: t('admin.missingInfoDescription'),
        variant: 'destructive',
      });
      return;
    }

    onPrepareSentences(
      title,
      description,
      germanText,
      imageData,
      originalLink || undefined
    );
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setOriginalLink('');
    setGermanText('');
    setImageData(null);
    setImportUrl('');
  };

  const disableActions = isProcessing || isImporting;

  return (
    <Card className="bg-gradient-card shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          {t('admin.createArticle')}
        </CardTitle>
        <CardDescription>{t('admin.createDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border border-dashed bg-white/40 p-4 space-y-3">
          <div className="space-y-1">
            <Label
              htmlFor="import-url"
              className="flex items-center gap-2 text-sm font-medium"
            >
              <LinkIcon className="h-4 w-4" />
              {t('admin.import.title')}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t('admin.import.description')}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="import-url"
              type="url"
              inputMode="url"
              placeholder={t('admin.import.placeholder')}
              value={importUrl}
              onChange={e => setImportUrl(e.target.value)}
              disabled={disableActions}
              onKeyDown={e => {
                if (e.key === 'Enter' && !disableActions) {
                  e.preventDefault();
                  handleImportFromUrl();
                }
              }}
              className="bg-white/70"
            />
            <Button
              type="button"
              onClick={handleImportFromUrl}
              disabled={disableActions || !importUrl.trim()}
              className="sm:w-32"
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('admin.import.loading')}
                </>
              ) : (
                t('admin.import.goButton')
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t('admin.articleTitle')}</Label>
            <Input
              id="title"
              placeholder={t('admin.titlePlaceholder')}
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="bg-white/50"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('admin.description')}</Label>
            <Input
              id="description"
              placeholder={t('admin.descriptionPlaceholder')}
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="bg-white/50"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="original-link">{t('admin.originalLink')}</Label>
          <Input
            id="original-link"
            type="url"
            placeholder="https://example.com/article"
            value={originalLink}
            onChange={e => setOriginalLink(e.target.value)}
            className="bg-white/50"
          />
          <p className="text-sm text-muted-foreground">
            {t('admin.originalLinkHint')}
          </p>
        </div>

        <ImageUpload
          onImageUpload={handleImageUpload}
          onImageRemove={handleImageRemove}
          currentImageUrl={imageData?.secure_url}
          className="space-y-2"
        />

        <div className="space-y-2">
          <Label htmlFor="german-text">{t('admin.germanText')}</Label>
          <Textarea
            id="german-text"
            placeholder={t('admin.germanTextPlaceholder')}
            value={germanText}
            onChange={e => setGermanText(e.target.value)}
            className="min-h-[200px] bg-white/50"
          />
          <p className="text-sm text-muted-foreground">{t('admin.textHint')}</p>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={resetForm}
            disabled={disableActions}
          >
            {t('admin.reset')}
          </Button>
          <Button
            onClick={handlePrepareSentences}
            disabled={disableActions || !title.trim() || !germanText.trim()}
            className="bg-gradient-hero hover:opacity-90 transition-opacity"
          >
            <Languages className="h-4 w-4 mr-2" />
            {t('admin.prepareSentences')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArticleCreation;
